import React, { useState, useRef, useEffect, useCallback } from 'react';
import { trimVideo, compressImage } from './MediaCompressor';

/**
 * VideoTrimmer
 * Shows a video preview with a dual-handle range slider to select start/end.
 * On confirm: trims the video (or compresses image) and calls onDone(blob, name, type).
 *
 * Props:
 *   file         - File object (video or image)
 *   onDone(blob, name, type) - called with processed result
 *   onCancel()   - called when user cancels
 */
export default function VideoTrimmer({ file, onDone, onCancel, limit = 50 * 1024 * 1024 }) {
  const isVideo = file?.type?.startsWith('video/');
  const isImage = file?.type?.startsWith('image/');
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragging, setDragging] = useState(null); // 'start' | 'end' | 'range' | null
  const [dragOffset, setDragOffset] = useState(0); // Offset for range dragging
  const [maxAllowedDuration, setMaxAllowedDuration] = useState(0);
  const sliderRef = useRef(null);

  // Create object URL for preview
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Load video duration
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    const v = videoRef.current;
    const onLoad = () => {
      setDuration(v.duration);
      
      // If file is too large, pre-select a safe range (guess based on size ratio)
      if (file && file.size > limit) {
        const ratio = limit / file.size;
        const safeDuration = v.duration * ratio * 0.95; // Use 95% of the ratio for a safety margin
        const initialMax = Math.min(v.duration, Math.max(Math.min(v.duration, 1), safeDuration));
        setMaxAllowedDuration(initialMax);
        setEndSec(initialMax);
      } else {
        setMaxAllowedDuration(v.duration);
        setEndSec(v.duration);
      }
    };
    v.addEventListener('loadedmetadata', onLoad);
    return () => v.removeEventListener('loadedmetadata', onLoad);
  }, [isVideo, previewUrl]);

  // Seek video to startSec when start thumb changes
  useEffect(() => {
    if (videoRef.current && isVideo) {
      videoRef.current.currentTime = startSec;
    }
  }, [startSec, isVideo]);

  const formatTime = (s) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Thumb drag handling on the slider track
  const getPercentFromEvent = useCallback((e) => {
    const track = sliderRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * duration;
  }, [duration]);

  const onMouseDown = (handle) => (e) => {
    e.preventDefault();
    setDragging(handle);
    if (handle === 'range') {
      const val = getPercentFromEvent(e);
      setDragOffset(val - startSec);
    }
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const val = getPercentFromEvent(e);
      if (dragging === 'start') {
        const minStart = Math.max(0, endSec - maxAllowedDuration);
        setStartSec(Math.max(minStart, Math.min(val, endSec - 0.1)));
      } else if (dragging === 'end') {
        const maxEnd = Math.min(duration, startSec + maxAllowedDuration);
        setEndSec(Math.min(maxEnd, Math.max(val, startSec + 0.1)));
      } else if (dragging === 'range') {
        const currentLen = endSec - startSec;
        let newStart = val - dragOffset;
        if (newStart < 0) newStart = 0;
        if (newStart + currentLen > duration) newStart = duration - currentLen;
        setStartSec(newStart);
        setEndSec(newStart + currentLen);
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, getPercentFromEvent, startSec, endSec]);

  const handleConfirm = async () => {
    setProcessing(true);
    setProgress(0);
    try {
      if (isVideo) {
        const blob = await trimVideo(file, startSec, endSec, (p) => setProgress(p));
        const ext = blob.type.includes('mp4') ? '.mp4' : '.webm';
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        onDone(blob, `${baseName}_trimmed${ext}`, blob.type);
      } else if (isImage) {
        const blob = await compressImage(file);
        onDone(blob, file.name, file.type);
      } else {
        onDone(file, file.name, file.type);
      }
    } catch (err) {
      alert('Processing failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const startPct = duration > 0 ? (startSec / duration) * 100 : 0;
  const endPct = duration > 0 ? (endSec / duration) * 100 : 100;

  return (
    <div style={S.overlay} onClick={(e) => e.stopPropagation()}>
      <div style={S.box}>
        {/* Header */}
        <div style={S.header}>
          <span style={S.title}>
            {isVideo ? '✂️ Trim Video' : '🖼️ Compress Image'}
          </span>
          <button style={S.closeBtn} onClick={onCancel}>✕</button>
        </div>

        {/* Preview */}
        {isVideo && previewUrl && (
          <div style={S.videoWrap}>
            <video
              ref={videoRef}
              src={previewUrl}
              style={S.video}
              muted
              playsInline
              onEnded={() => {
                if (videoRef.current) videoRef.current.currentTime = startSec;
              }}
            />
            <button style={S.playBtn}
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                v.currentTime = startSec;
                v.play().then(() => {
                  const check = () => {
                    if (v.currentTime >= endSec) { v.pause(); return; }
                    requestAnimationFrame(check);
                  };
                  check();
                });
              }}
            >
              ▶ Preview Clip
            </button>
          </div>
        )}

        {isImage && previewUrl && (
          <div style={S.videoWrap}>
            <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, objectFit: 'contain' }} />
          </div>
        )}

        {/* Slider (only for video) */}
        {isVideo && duration > 0 && (
          <div style={S.sliderSection}>
            <div style={S.timeRow}>
              <span style={S.timeLabel}>{formatTime(startSec)}</span>
              <span style={{ color: (endSec - startSec >= maxAllowedDuration * 0.99) ? '#f87171' : '#94a3b8', fontSize: 10, fontWeight: 600 }}>
                {endSec - startSec >= maxAllowedDuration * 0.99 ? '⚠️ Max Length Reached' : `Selection: ${formatTime(endSec - startSec)}`}
              </span>
              <span style={S.timeLabel}>{formatTime(endSec)}</span>
            </div>

            {/* Track */}
            <div ref={sliderRef} style={S.track} onMouseDown={(e) => {
              // Detect click on track to move nearest thumb
              const val = getPercentFromEvent(e);
              if (Math.abs(val - startSec) < Math.abs(val - endSec)) {
                const minStart = Math.max(0, endSec - maxAllowedDuration);
                setStartSec(Math.max(minStart, Math.min(val, endSec - 0.1)));
              } else {
                const maxEnd = Math.min(duration, startSec + maxAllowedDuration);
                setEndSec(Math.min(maxEnd, Math.max(val, startSec + 0.1)));
              }
            }}>
              {/* Inactive left */}
              <div style={{ ...S.trackFill, left: 0, width: `${startPct}%`, background: 'rgba(255,255,255,0.08)' }} />
              
              {/* Active range - Draggable */}
              <div 
                style={{ ...S.trackFill, left: `${startPct}%`, width: `${endPct - startPct}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', cursor: 'grab' }} 
                onMouseDown={onMouseDown('range')}
                onTouchStart={onMouseDown('range')}
              >
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <div style={{ width: 12, height: 2, background: 'rgba(255,255,255,0.7)', borderRadius: 1 }} />
                  {/* Down pointer */}
                  <div style={{ 
                    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                    width: 0, height: 0, 
                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                    borderTop: '6px solid #ffffff' 
                  }} />
                  <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', fontSize: 8, color: '#ffffff', fontWeight: 800, whiteSpace: 'nowrap' }}>DRAG</div>
                </div>
              </div>

              {/* Inactive right */}
              <div style={{ ...S.trackFill, left: `${endPct}%`, width: `${100 - endPct}%`, background: 'rgba(255,255,255,0.08)' }} />

              {/* Start thumb */}
              <div
                style={{ ...S.thumb, left: `${startPct}%`, background: '#818cf8' }}
                onMouseDown={onMouseDown('start')}
                onTouchStart={onMouseDown('start')}
              >
                <span style={S.thumbLabel}>{formatTime(startSec)}</span>
              </div>

              {/* End thumb */}
              <div
                style={{ ...S.thumb, left: `${endPct}%`, background: '#a78bfa' }}
                onMouseDown={onMouseDown('end')}
                onTouchStart={onMouseDown('end')}
              >
                <span style={{ ...S.thumbLabel, top: 'auto', bottom: 22 }}>{formatTime(endSec)}</span>
              </div>
            </div>

            <div style={S.timeRow}>
              <span style={{ color: '#475569', fontSize: 10 }}>0:00</span>
              <span style={{ color: '#475569', fontSize: 10 }}>{formatTime(duration)}</span>
            </div>
          </div>
        )}

        {isImage && (
          <div style={{ padding: '8px 0', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>
            Image will be compressed to reduce file size
          </div>
        )}

        {/* Progress */}
        {processing && (
          <div style={S.progressWrap}>
            <div style={{ ...S.progressBar, width: `${progress}%` }} />
            <span style={S.progressText}>{progress}%</span>
          </div>
        )}

        {/* Actions */}
        <div style={S.actions}>
          <button style={S.cancelBtn} onClick={onCancel} disabled={processing}>
            Cancel
          </button>
          <button style={S.confirmBtn} onClick={handleConfirm} disabled={processing}>
            {processing
              ? (isVideo ? `Trimming... ${progress}%` : 'Compressing...')
              : (isVideo ? '✂️ Trim & Use' : '🖼️ Compress & Use')}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  box: {
    background: '#111118', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20, width: '100%', maxWidth: 500, padding: 24,
    boxShadow: '0 25px 60px rgba(0,0,0,0.7)',
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontWeight: 700, fontSize: 15, color: '#e2e8f0' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1 },
  videoWrap: {
    borderRadius: 12, overflow: 'hidden', background: '#000',
    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 140,
  },
  video: { width: '100%', maxHeight: 220, display: 'block', objectFit: 'contain' },
  playBtn: {
    position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
    background: 'rgba(99,102,241,0.85)', border: 'none', color: '#fff',
    padding: '6px 18px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
  sliderSection: { display: 'flex', flexDirection: 'column', gap: 8 },
  timeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  timeLabel: { fontSize: 10, fontWeight: 700, color: '#818cf8' },
  track: {
    position: 'relative', height: 8, borderRadius: 4,
    background: 'rgba(255,255,255,0.08)', cursor: 'pointer', margin: '12px 0',
    userSelect: 'none',
  },
  trackFill: { position: 'absolute', top: 0, height: '100%', borderRadius: 4 },
  thumb: {
    position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
    width: 20, height: 20, borderRadius: '50%',
    border: '3px solid #fff', cursor: 'grab', zIndex: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    touchAction: 'none',
  },
  thumbLabel: {
    position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)',
    fontSize: 10, fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap',
    background: 'rgba(0,0,0,0.7)', padding: '1px 5px', borderRadius: 4,
  },
  progressWrap: {
    position: 'relative', height: 6, borderRadius: 3,
    background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
  },
  progressBar: {
    position: 'absolute', top: 0, left: 0, height: '100%',
    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
    transition: 'width 0.2s ease', borderRadius: 3,
  },
  progressText: {
    position: 'absolute', right: 0, top: -18,
    fontSize: 10, color: '#818cf8', fontWeight: 700,
  },
  actions: { display: 'flex', gap: 10, marginTop: 4 },
  cancelBtn: {
    flex: 1, padding: '11px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  confirmBtn: {
    flex: 2, padding: '11px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700,
  },
};
